import { Site } from '@envato/foundation-types'
import get from 'lodash-es/get'
import isEmpty from 'lodash-es/isEmpty'

import experimentsConfig from '../../config/experiments'
import sitesConfig from '../../config/sites'
import { IExperimentsState } from '../reducers/experiments'
import { ICommonLocation } from './location/types'

export interface IFeaturesConfig {
  [featureName: string]: boolean
}

/**
 * Returns `IFeaturesConfig` based on purely `location.query.features` param
 */
export const getLocationFeaturesConfig = (
  location: ICommonLocation
): IFeaturesConfig => {
  const locationString = get(location, 'query.features', '') as string
  const locationArray = locationString
    .split(',')
    .filter((value: any) => !isEmpty(value))
  const locationConfig = locationArray.reduce<IFeaturesConfig>(
    (config: any, featureName: string) => {
      if (featureName[0] === '!') {
        featureName = featureName.slice(1)
        config[featureName] = false
        return config
      }

      config[featureName] = true
      return config
    },
    {}
  )
  return locationConfig
}

/**
 * Returns `IFeaturesConfig` based on purely `forced_features` section from static site config
 * (config/sites/*)
 */
export const getSitesForcedFeaturesConfig = (
  currentSite: Site
): IFeaturesConfig => {
  const defaultConfig = get(sitesConfig, 'defaults.forced_features', {})
  const siteConfig = get(sitesConfig, `${currentSite}.forced_features`, {})

  return {
    ...defaultConfig,
    ...siteConfig
  }
}

/**
 * Returns `IFeaturesConfig` based on purely `features` section from static site config
 * (config/sites/*)
 */
export const getSitesFeaturesConfig = (currentSite: Site): IFeaturesConfig => {
  const defaultConfig = get(sitesConfig, 'defaults.features', {})
  const siteConfig = get(sitesConfig, `${currentSite}.features`, {})

  return {
    ...defaultConfig,
    ...siteConfig
  }
}

/**
 * Returns `IFeaturesConfig` based on `IExperimentsState` from redux store and
 * experiments `IVariantsToFeaturesConfig` mapping
 */
export const getExperimentsFeaturesConfig = (
  experimentsState: IExperimentsState
): IFeaturesConfig =>
  Object.keys(experimentsConfig)
    .sort(cfg => (experimentsConfig[cfg].requires ? 1 : -1))
    .reduce<IFeaturesConfig>(
      (featuresConfig: IFeaturesConfig, experimentName: string) => {
        const experimentConfig = experimentsConfig[experimentName]
        const experimentId = experimentConfig.experimentId
        const experimentState = experimentsState[experimentId]
        const experimentVariants = experimentConfig.variants

        if (experimentState && typeof experimentState === 'object') {
          const experimentVariant = Number(experimentState.variant)
          const nextFeaturesConfig = experimentVariants[experimentVariant]
          return {
            ...featuresConfig,
            ...nextFeaturesConfig
          }
        }

        return featuresConfig
      },
      {}
    )

export const getFeaturesConfig = (
  currentSite: Site,
  location: ICommonLocation,
  experimentsState: IExperimentsState
) => {
  const mainFeaturesConfig = getSitesFeaturesConfig(currentSite)
  const experimentsFeaturesConfig = getExperimentsFeaturesConfig(
    experimentsState
  )
  const forcedFeaturesConfig = getSitesForcedFeaturesConfig(currentSite)
  const locationFeaturesConfig = getLocationFeaturesConfig(location)

  return {
    ...mainFeaturesConfig,
    ...experimentsFeaturesConfig,
    ...forcedFeaturesConfig,
    ...locationFeaturesConfig
  }
}

export const hasFeature = (
  name: string,
  currentSite: Site,
  location: ICommonLocation,
  experimentsState: IExperimentsState = { experimentsReady: false }
): boolean => {
  return !!getFeaturesConfig(currentSite, location, experimentsState)[name]
}
